import { type SyntheticEvent, useState } from 'react'
import { FormattedMessage, FormattedNumber, useIntl } from 'react-intl'
import { useQueryClient } from '@tanstack/react-query'
import { isAxiosError } from 'axios'

import { Typography } from '@gto/ui/typography'

import type { AffiliateAdminCommissionCorrectionResponseSchema } from '@/api/gen'
import {
  getGetPartnerQueryKey,
  getGetPartnerStatsViewQueryKey,
  getListPartnerCommissionsQueryKey,
  useCreatePartnerCommissionCorrection,
} from '@/api/gen'
import { TextareaField, TextField } from '@/components/form'
import { MutationError } from '@/components/mutation-error'
import { SectionCard } from '@/components/section-card'
import { useMutationErrorHandler } from '@/hooks/use-mutation-error-handler'
import type { ParseSignedAmountReason } from '@/utils/parse-signed-amount'
import { parseSignedAmount } from '@/utils/parse-signed-amount'
import { toAmount } from '@/utils/to-amount'

import { SectionHeader } from '../section-header'
import { StackedField } from '../stacked-field'

const MEMO_MAX_LENGTH = 500

// Map each parse failure to its inline message. A Record keyed on the parser's
// own reason union (not a ternary chain) keeps the mapping exhaustive: adding a
// reason to ParseSignedAmountReason without a message here is a type error.
const AMOUNT_ERROR_MESSAGE_IDS: Record<ParseSignedAmountReason, string> = {
  empty: 'admin.partnerDetail.balanceCorrection.amount.empty',
  zero: 'admin.partnerDetail.balanceCorrection.amount.zero',
  'too-many-decimals': 'admin.partnerDetail.balanceCorrection.amount.tooManyDecimals',
  overflow: 'admin.partnerDetail.balanceCorrection.amount.overflow',
  'not-finite': 'admin.partnerDetail.balanceCorrection.amount.invalid',
}

/**
 * Pull the BE detail string out of a Ninja CustomValidationError 422 so the
 * operator sees an actionable reason (e.g. "Amount exceeds available balance")
 * instead of the generic fallback. Observability stays in
 * `useMutationErrorHandler` — this is presentation only.
 */
const extractCorrectionDetail = (err: unknown): string | null => {
  if (!isAxiosError(err)) {
    return null
  }
  const data = err.response?.data as { detail?: unknown; message?: unknown } | undefined
  if (typeof data?.detail === 'string' && data.detail.length > 0) {
    return data.detail
  }
  if (typeof data?.message === 'string' && data.message.length > 0) {
    return data.message
  }

  return null
}

type BalanceCorrectionCardProps = {
  className?: string
  partnerId: string
}

/**
 * Admin form to record a signed manual balance correction. Submits to
 * `POST /admin/partners/{partnerId}/commissions/correction/`. On success the
 * BE returns the partner's new `available_balance`, which we display inline
 * and use to invalidate the partner-stats cache so the Overview balance card
 * refreshes — same UX as the tier-commission card's PATCH flow.
 *
 * Validation is intentionally client-side AND server-side: parseSignedAmount
 * catches obvious shape errors before they hit the BE (empty, zero, NaN,
 * >2 decimals, >9 integer digits); a 422 from the BE (e.g. "exceeds
 * available balance") is surfaced via `extractCorrectionDetail`.
 */
export const BalanceCorrectionCard = ({ className, partnerId }: BalanceCorrectionCardProps) => {
  const { formatMessage } = useIntl()
  const queryClient = useQueryClient()
  const [editing, setEditing] = useState(false)
  const [amount, setAmount] = useState('')
  const [memo, setMemo] = useState('')
  const [amountInvalid, setAmountInvalid] = useState<ParseSignedAmountReason | null>(null)
  const [memoInvalid, setMemoInvalid] = useState(false)
  const [lastBalance, setLastBalance] = useState<number | null>(null)

  const { hasError, detail, onError, clearError } = useMutationErrorHandler(
    'createPartnerCommissionCorrection failed: ',
    { extractDetail: extractCorrectionDetail },
  )

  const mutation = useCreatePartnerCommissionCorrection({
    mutation: {
      onMutate: clearError,
      onSuccess: (response: AffiliateAdminCommissionCorrectionResponseSchema | undefined) => {
        // The OpenAPI type marks `available_balance` non-nullable, but a 200
        // with no body / a 204 deserialises to `undefined` here. Reading
        // `.available_balance` off that would throw INSIDE onSuccess — which
        // React Query does NOT route to onError — leaving the operator with a
        // crash AFTER the correction already committed server-side. Guard it:
        // skip the inline balance display when the body is absent (the cache
        // invalidation below still refreshes every balance surface), rather
        // than fabricate a $0.00.
        const balance = response?.available_balance
        if (balance != null) {
          setLastBalance(toAmount(balance, 'balance-correction-card available_balance:'))
        }
        // Invalidate every cache that depends on the partner's balance — the
        // partner detail (totals), the stats view (the Overview balance card),
        // and the commissions tab (the new manual commission row).
        void queryClient.invalidateQueries({ queryKey: getGetPartnerQueryKey(partnerId) })
        void queryClient.invalidateQueries({
          queryKey: getGetPartnerStatsViewQueryKey(partnerId),
        })
        void queryClient.invalidateQueries({
          queryKey: getListPartnerCommissionsQueryKey(partnerId),
        })
        setEditing(false)
        setAmount('')
        setMemo('')
      },
      onError,
    },
  })

  const startEditing = () => {
    setAmount('')
    setMemo('')
    setAmountInvalid(null)
    setMemoInvalid(false)
    // Clear the previous correction's "balance after correction" so a stale
    // figure from an earlier edit session can't linger behind a fresh form.
    setLastBalance(null)
    setEditing(true)
    mutation.reset()
  }

  const handleSubmit = (e: SyntheticEvent) => {
    e.preventDefault()
    // Guard the form-submit path (not just the disabled Save button): a
    // double-Enter on the number field could otherwise double-fire a
    // non-idempotent money POST before the re-render disables the button.
    if (mutation.isPending) {
      return
    }
    const parsed = parseSignedAmount(amount)
    const memoTrimmed = memo.trim()
    const memoOk = memoTrimmed.length > 0 && memoTrimmed.length <= MEMO_MAX_LENGTH

    if (!parsed.ok || !memoOk) {
      setAmountInvalid(parsed.ok ? null : parsed.reason)
      setMemoInvalid(!memoOk)

      return
    }

    setAmountInvalid(null)
    setMemoInvalid(false)
    mutation.mutate({
      partnerId,
      data: { amount: parsed.value, memo: memoTrimmed },
    })
  }

  return (
    <SectionCard
      className={className}
      onSubmit={editing ? handleSubmit : undefined}
    >
      <SectionHeader
        editing={editing}
        saving={mutation.isPending}
        title={<FormattedMessage id="admin.partnerDetail.balanceCorrection" />}
        onCancel={() => setEditing(false)}
        onEdit={startEditing}
        onSave={() => undefined}
      />
      {editing ? (
        <div className="flex flex-col gap-3">
          <StackedField
            label={formatMessage({ id: 'admin.partnerDetail.balanceCorrection.amount' })}
          >
            <TextField
              data-tst="balance-correction-amount"
              helperText={formatMessage({ id: 'admin.partnerDetail.balanceCorrection.amountHint' })}
              inputMode="decimal"
              step={0.01}
              type="number"
              value={amount}
              error={
                amountInvalid === null
                  ? undefined
                  : formatMessage({ id: AMOUNT_ERROR_MESSAGE_IDS[amountInvalid] })
              }
              onChange={(e) => {
                setAmount(e.target.value)
                if (amountInvalid !== null) {
                  setAmountInvalid(null)
                }
              }}
            />
          </StackedField>
          <StackedField label={formatMessage({ id: 'admin.partnerDetail.balanceCorrection.memo' })}>
            <TextareaField
              required
              data-tst="balance-correction-memo"
              maxLength={MEMO_MAX_LENGTH}
              rows={2}
              value={memo}
              error={
                memoInvalid
                  ? formatMessage({ id: 'admin.partnerDetail.balanceCorrection.memo.required' })
                  : undefined
              }
              helperText={formatMessage(
                { id: 'admin.partnerDetail.balanceCorrection.memoHint' },
                { max: MEMO_MAX_LENGTH },
              )}
              onChange={(e) => {
                setMemo(e.target.value)
                if (memoInvalid) {
                  setMemoInvalid(false)
                }
              }}
            />
          </StackedField>
          <MutationError
            data-tst="balance-correction-error"
            detail={detail}
            messageId="admin.partnerDetail.balanceCorrection.error"
            show={hasError}
          />
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          <Typography
            color="text.secondary"
            variant="body2"
          >
            <FormattedMessage id="admin.partnerDetail.balanceCorrection.description" />
          </Typography>
          {lastBalance !== null && (
            <StackedField
              label={formatMessage({ id: 'admin.partnerDetail.balanceCorrection.newBalance' })}
            >
              <Typography
                className="font-mono"
                color="primary"
                data-tst="balance-correction-new-balance"
                variant="body2"
              >
                <FormattedNumber
                  currency="USD"
                  style="currency"
                  value={lastBalance}
                />
              </Typography>
            </StackedField>
          )}
        </div>
      )}
    </SectionCard>
  )
}
